import { Link } from "react-router-dom";
import { HexBg, GlowLayer } from "../components/Decor";
import { CtaSection, ImageBand } from "../components/Sections";
import Seo from "../components/Seo";
import ContactForm, { ContactDetails } from "../components/ContactForm";
import CountUp from "../components/CountUp";
import { SERVICES, INDUSTRIES, PROCESS, VALUES, SYSTEMS, IMAGES, PAGE_SEO } from "../lib/data";

export default function Home() {
  return (
    <>
      <Seo {...PAGE_SEO.home} />
      {/* HERO */}
      <section id="hero">
        <HexBg />
        <div className="hero-content">
          <div>
            <div className="eyebrow"><span className="eyebrow-dot"></span>Supply Chain Systems Consulting</div>
            <h1>Turning Warehouse Complexity Into <span className="accent">Operational Excellence</span></h1>
            <p className="hero-sub">Supply chain systems consulting for warehouses, distributors, and 3PLs.</p>
            <p className="hero-spec">Specializing in WMS Optimization, Warehouse Process Improvement, Systems Integration, and Fractional Supply Chain Support.</p>
            <div className="hero-ctas">
              <Link to="/contact" className="btn-primary" data-testid="hero-schedule-btn">Schedule a Discovery Call ↗</Link>
              <Link to="/services" className="btn-ghost" data-testid="hero-services-btn">Explore Services</Link>
            </div>
          </div>
          <div className="hero-tiles">
            <div className="hero-tile featured">
              <div className="tile-num">WMS</div>
              <div className="tile-label">Implementation & Health Checks</div>
              <div className="tile-sub">Plus TMS · ERP · EDI · API Integrations</div>
            </div>
            <div className="hero-tile">
              <div className="tile-num">Multi</div>
              <div className="tile-label">Industry Experience</div>
              <div className="tile-sub">3PL · Distribution · Ecomm · Mfg · Shippers</div>
            </div>
            <div className="hero-tile wide">
              <div>
                <div className="tile-num">Ops + IT</div>
                <div className="tile-label">Operations & Technology Expertise</div>
              </div>
              <div className="tile-divider"></div>
              <div>
                <div className="tile-num">Flex</div>
                <div className="tile-label">Fractional or Project-Based</div>
                <div className="tile-sub">Hourly · Retainer · Fixed-Scope Projects</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about">
        <div className="s-inner">
          <div className="about-grid">
            <div className="fade-up">
              <span className="s-eye">About Montivex</span>
              <h2>We bridge the gap between warehouse operations and enterprise systems.</h2>
              <p className="s-lead">Many consultants understand systems. Many warehouse managers understand operations. Montivex understands both — and focuses on real-world execution, business outcomes, and measurable ROI.</p>
              <div className="about-pillars">
                {VALUES.map((v) => {
                  const Icon = v.icon;
                  return (
                    <div className="pillar" key={v.title}>
                      <div className="pillar-icon"><Icon size={18} /></div>
                      <div>
                        <h4>{v.title}</h4>
                        <p>{v.text}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="about-visual fade-up d2">
              <div className="about-card">
                <h3>Deep expertise across the full supply chain tech stack.</h3>
                <p>From WMS configuration to EDI monitoring to dashboard development — we provide practical, hands-on expertise across every layer of your supply chain systems environment.</p>
                <div className="exp-tags">
                  {SYSTEMS.map((s, i) => (
                    <span className={`exp-tag ${i < 4 ? "hi" : ""}`} key={s}>{s}</span>
                  ))}
                </div>
              </div>
              <div className="about-float">
                <strong>End-to-End</strong>
                <span>Supply chain coverage</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section id="stats" style={{ position: "relative", overflow: "hidden" }}>
        <GlowLayer />
        <div className="s-inner" style={{ position: "relative" }}>
          <div className="stats-hdr fade-up">
            <span className="s-eye on-dark">Why It Matters</span>
            <h2>The gap between systems and operations is where performance is lost</h2>
            <p className="s-lead">We focus on the metrics that actually move your business forward.</p>
          </div>
          <div className="stats-grid fade-up d1">
            <div className="metric hi">
              <div className="m-num"><CountUp target={99} suffix="%" /></div>
              <div className="m-label">Target Inventory Accuracy</div>
              <div className="m-sub">Post-optimization baseline</div>
            </div>
            <div className="metric">
              <div className="m-num"><CountUp target={35} suffix="%" /></div>
              <div className="m-label">Avg. Labor Cost Reduction</div>
              <div className="m-sub">Through process optimization</div>
            </div>
            <div className="metric">
              <div className="m-num"><CountUp target={40} suffix="+" /></div>
              <div className="m-label">Hrs/Month Per Analyst</div>
              <div className="m-sub">Scale with multiple analysts as needed</div>
            </div>
            <div className="metric">
              <div className="m-num">3</div>
              <div className="m-label">Engagement Options</div>
              <div className="m-sub">Hourly · Retainer · Fixed-Scope</div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services">
        <div className="s-inner">
          <div className="services-hdr">
            <div className="fade-up">
              <span className="s-eye">Our Services</span>
              <h2>Five ways we help supply chain organizations perform better</h2>
            </div>
            <p className="s-lead fade-up d1">From a one-time health check to an ongoing fractional partnership — we meet you where you are.</p>
          </div>
          <div className="services-grid">
            {SERVICES.slice(0, 4).map((s, i) => {
              const Icon = s.icon;
              return (
                <div className={`svc-card fade-up ${i === 1 ? "d1" : i === 2 ? "d2" : ""}`} key={s.slug}>
                  <div className="svc-icon"><Icon size={22} /></div>
                  <h3>{s.title}</h3>
                  <p>{s.short}</p>
                  <ul className="svc-list">
                    {s.points.map((p) => <li key={p}>{p}</li>)}
                  </ul>
                  <Link to="/contact" className="svc-link">{s.cta} →</Link>
                </div>
              );
            })}

            {/* 5th: dark span2 */}
            <div className="svc-card dark span2 fade-up d1">
              <div className="svc-inner">
                <div className="svc-left">
                  <div className="svc-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#00B4D8" strokeWidth="2"><circle cx="12" cy="12" r="3" /><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" /></svg>
                  </div>
                  <h3>Fractional Supply Chain Systems Analyst</h3>
                  <p>Act as an extension of your supply chain systems team — providing expert coverage across WMS, TMS, ERP, EDI, API integrations, and reporting platforms on a flexible monthly basis.</p>
                  <Link to="/services" className="svc-link">Learn More →</Link>
                </div>
                <div>
                  <p style={{ fontSize: "0.8rem", fontWeight: 700, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>Systems Covered</p>
                  <ul className="svc-list" style={{ marginBottom: 20 }}>
                    <li>WMS &amp; TMS</li>
                    <li>ERP &amp; EDI monitoring</li>
                    <li>API Integrations</li>
                    <li>Reporting platforms &amp; dashboards</li>
                  </ul>
                  <p style={{ fontSize: "0.8rem", fontWeight: 700, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>What We Do</p>
                  <ul className="svc-list">
                    <li>Integration support &amp; root-cause analysis</li>
                    <li>Data analysis &amp; dashboard development</li>
                    <li>Vendor management</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section id="industries">
        <div className="s-inner">
          <div className="ind-hdr fade-up">
            <span className="s-eye">Industries We Serve</span>
            <h2>Built for the operations that keep supply chains moving</h2>
            <p className="s-lead">We work specifically with the warehouse types where supply chain complexity meets real operational pressure.</p>
          </div>
          <div className="ind-grid">
            {INDUSTRIES.map((ind, i) => (
              <div className={`ind-card fade-up ${i ? "d" + i : ""}`} key={ind.title}>
                <div className="ind-icon">{ind.icon}</div>
                <h3>{ind.title}</h3>
                <ul className="ind-pains">
                  {ind.pains.map((p) => <li key={p}>{p}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process">
        <div className="s-inner">
          <div className="process-hdr fade-up">
            <span className="s-eye">How We Work</span>
            <h2>A process built around your operation</h2>
            <p className="s-lead">No black boxes. No scope creep surprises. A clear path from current state to measurable results.</p>
          </div>
          <div className="steps">
            {PROCESS.map((step, i) => (
              <div className={`step fade-up ${i ? "d" + i : ""}`} key={step.n}>
                <div className="step-bubble">{step.n}</div>
                <h4>{step.title}</h4>
                <p>{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ImageBand
        image={IMAGES.homeBand}
        eyebrow="End-to-End Visibility"
        title="From the loading dock to the dashboard"
        text="We connect what happens on the warehouse floor with the systems leadership relies on — so the data you report is the reality you run. Practical optimization across people, process, and technology."
        points={[
          "Receiving, putaway, picking & shipping optimization",
          "WMS, TMS, ERP & EDI integration health",
          "Real-time inventory accuracy & KPI dashboards",
          "Scalable processes that grow with your volume",
        ]}
      />

      <CtaSection />

      {/* CONTACT */}
      <section id="contact">
        <div className="s-inner">
          <div className="contact-grid">
            <div className="fade-up">
              <span className="s-eye">Contact</span>
              <h2>Tell us about your current challenge</h2>
              <p style={{ fontSize: "0.93rem", color: "var(--text-muted)", lineHeight: 1.7, marginBottom: 36 }}>We respond within one business day. Most discovery calls take 60 minutes and end with a clear set of next steps — whether we work together or not.</p>
              <ContactDetails />
            </div>
            <ContactForm />
          </div>
        </div>
      </section>
    </>
  );
}
